import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chargedRate, VAT_CODES } from '@fsp/core';
import { useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { ApiError, api, type Me } from '../api';
import { basisPoints, cents, money, moneyInput, nullIfEmpty, rateInput, readForm } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell, ThirdPane } from '../layout';
import { fillFields, useRegistry } from './registry';

export interface Company { id: number; hasLogo: boolean; name: string; code: string | null; vatCode: string | null; isVatPayer: boolean; vatPayerFrom: string | null; address: string | null; email: string | null; phone: string | null; issuerName: string | null; bankName: string | null; iban: string | null; swift: string | null }
export interface Project { id: number; name: string; code: string; color: string; categoryId: number | null; parentProjectId: number | null; emailFrom: string | null; emailName: string | null; emailCopy: boolean; smtpHost: string | null; smtpPort: number | null; smtpSecurity: 'tls' | 'ssl' | 'none' | null; smtpUser: string | null; hasSmtpPassword: boolean; hasLogo: boolean; bankName: string | null; iban: string | null; swift: string | null }
export interface Service { id: number; projectId: number | null; name: string; description: string | null; unit: string; unitPrice: number; vatRate: number; vatCode: string | null }
export interface Template { id: number; projectId: number | null; name: string; kind: 'invoice' | 'vat_invoice' | 'proforma' | 'credit'; series: string; nextNumber: number; numberWidth: number; paymentDays: number; note: string; isDefault: boolean }

export const useProjects = () => useQuery({ queryKey: ['projects'], queryFn: () => api.get<{ projects: Project[] }>('api/v1/projects').then((r) => r.projects) });
export const useServices = () => useQuery({ queryKey: ['services'], queryFn: () => api.get<{ services: Service[] }>('api/v1/services').then((r) => r.services) });
export const useTemplates = () => useQuery({ queryKey: ['templates'], queryFn: () => api.get<{ templates: Template[] }>('api/v1/templates').then((r) => r.templates) });
export const useCompany = () => useQuery({ queryKey: ['company'], queryFn: () => api.get<{ company: Company }>('api/v1/company').then((r) => r.company) });

export const KIND_LABELS: Record<Template['kind'], string> = {
  invoice: 'Sąskaita faktūra', vat_invoice: 'PVM sąskaita faktūra', proforma: 'Išankstinė sąskaita', credit: 'Kreditinė sąskaita',
};

/** List on the left, create/edit form on the right; the pattern of every catalog page. */
function useEditor<T extends { id: number }>(path: string, key: string) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<T | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: unknown) => (editing ? api.put(`${path}/${editing.id}`, body) : api.post(path, body)),
    onSuccess: async () => { setEditing(null); setNotice('saved'); await queryClient.invalidateQueries({ queryKey: [key] }); },
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`${path}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [key] }),
  });
  return { editing, setEditing: (row: T | null) => { setEditing(row); setNotice(null); save.reset(); }, save, remove, notice };
}

function EditorPane({ title, editing, error, onCancel, children }: { title: [string, string]; editing: boolean; error: unknown; onCancel: () => void; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <ThirdPane className="panel-card customer-add" label={t(title[0])} open={editing}>
      <p className="eyebrow">{editing ? t('Redagavimas') : t('Naujas')}</p>
      <h2>{t(editing ? title[1] : title[0])}</h2>
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      {children}
      {editing ? <button type="button" className="button button--ghost" onClick={onCancel}>{t('Atšaukti')}</button> : null}
    </ThirdPane>
  );
}

export function CompanyPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const company = useCompany();
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (body: unknown) => api.put('api/v1/company', body),
    onSuccess: async () => { setSaved(true); await queryClient.invalidateQueries({ queryKey: ['company'] }); await queryClient.invalidateQueries({ queryKey: ['me'] }); },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    const v = readForm(event);
    // Becoming a VAT payer without a date: from today (an existing payer without a date stays "from the start").
    const payer = v.isVatPayer === '1';
    const vatPayerFrom = payer ? v.vatPayerFrom || (company.data?.isVatPayer ? null : new Date().toLocaleDateString('sv-SE')) : null;
    save.mutate({ name: v.name, code: v.code, vatCode: v.vatCode, isVatPayer: payer, vatPayerFrom, address: v.address, email: v.email, phone: v.phone, issuerName: v.issuerName, bankName: v.bankName, iban: v.iban, swift: v.swift });
  };
  const c = company.data;
  const formRef = useRef<HTMLFormElement>(null);
  // Own company: the VAT register also says whether the company is a VAT payer.
  const registry = useRegistry((found) => {
    fillFields(formRef.current, { name: 'name', code: 'code', vatCode: 'vatCode', address: 'address' }, found);
    const payer = formRef.current?.elements.namedItem('isVatPayer');
    if (payer instanceof HTMLInputElement) payer.checked = Boolean(found.vatCode);
    const vat = formRef.current?.elements.namedItem('vatCode');
    if (vat instanceof HTMLInputElement && !found.vatCode) vat.value = '';
  });
  const fact = (label: string, value: string | null | undefined) => <div className="company-fact"><span>{t(label)}</span><strong>{value || '—'}</strong></div>;
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Nustatymai')} title={t('{company} duomenys', { company: c?.name ?? me.company.name })} text={t('Bendri pardavėjo rekvizitai. Pagrindinės banko sąskaitos valdomos atskirai kiekvieno projekto nustatymuose.')} />}>
      {saved ? <Alert kind="success">{t('Įmonės duomenys išsaugoti.')}</Alert> : null}
      {c ? (
        <div className="customer-layout">
          <section className="panel-card"><div className="customer-list"><article className="customer-row company-row">
            <div className="customer-avatar">{c.hasLogo ? <img src={`api/v1/company/logo?v=${company.dataUpdatedAt}`} alt="" /> : c.name.slice(0, 2).toUpperCase()}</div>
            <div className="customer-main"><h3>{c.name}</h3><p>{c.code || t('Įmonės kodas nenurodytas')}{c.vatCode ? ` · ${c.vatCode}` : ''}</p><small>{c.isVatPayer ? `${t('PVM mokėtojas')}${c.vatPayerFrom ? ` ${t('nuo')} ${c.vatPayerFrom}` : ''}` : t('Ne PVM mokėtojas')}</small></div>
            <div className="company-facts party-facts">
              {fact('Adresas', c.address)}{fact('El. paštas', c.email)}{fact('Telefonas', c.phone)}{fact('Sąskaitą išrašo', c.issuerName)}
              {fact('Atsarginis bankas', c.bankName)}{fact('Atsarginis IBAN', c.iban)}{fact('SWIFT / BIC', c.swift)}
            </div>
          </article></div>
          <CompanyLogo company={c} /></section>
          <ThirdPane className="panel-card customer-add" label={t('Redaguoti įmonės duomenis')}>
            <p className="eyebrow">{t('Redagavimas')}</p><h2>{t('Redaguoti įmonės duomenis')}</h2>
            {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
            <form onSubmit={submit} ref={formRef}>
              <label>{t('Įmonės pavadinimas')}<input name="name" defaultValue={c.name} required /></label>
              <div className="form-grid">
                <label>{t('Įmonės kodas')}<span className="input-with-button"><input name="code" defaultValue={c.code ?? ''} />{registry.button(() => (formRef.current?.elements.namedItem('code') as HTMLInputElement | null)?.value ?? '')}</span></label>
                <label>{t('PVM kodas')}<input name="vatCode" defaultValue={c.vatCode ?? ''} placeholder={t('Jei netaikoma — palikite tuščią')} /></label>
              </div>
              <div className="form-grid vat-payer-row">
                <label className="remember-device"><input type="checkbox" name="isVatPayer" value="1" defaultChecked={c.isVatPayer} /><span>{t('Įmonė yra PVM mokėtoja')}</span></label>
                <label>{t('PVM mokėtoja nuo')}<input type="date" name="vatPayerFrom" defaultValue={c.vatPayerFrom ?? ''} title={t('Nuo šios dienos tiekėjų sąskaitų PVM atskaitomas, o išrašomose sąskaitose skaičiuojamas PVM. Tuščia — nuo pradžios.')} /></label>
              </div>
              <label>{t('Adresas')}<textarea name="address" rows={2} defaultValue={c.address ?? ''} /></label>
              {registry.extras}
              <p className="form-section">{t('Kontaktai')}</p>
              <div className="form-grid">
                <label>{t('El. paštas')}<input type="email" name="email" defaultValue={c.email ?? ''} /></label>
                <label>{t('Telefonas')}<input type="tel" name="phone" defaultValue={c.phone ?? ''} /></label>
                <label className="span-2">{t('Sąskaitą išrašo')}<input name="issuerName" defaultValue={c.issuerName ?? ''} /></label>
              </div>
              <p className="form-section">{t('Atsarginiai banko rekvizitai')}</p>
              <div className="form-grid">
                <label>{t('Atsarginis bankas')}<input name="bankName" defaultValue={c.bankName ?? ''} /></label>
                <label>SWIFT / BIC<input name="swift" defaultValue={c.swift ?? ''} /></label>
                <label className="span-2">{t('Atsarginis IBAN')}<input name="iban" defaultValue={c.iban ?? ''} /></label>
              </div>
              <div className="form-actions"><button className="button button--primary" disabled={save.isPending}>{t('Išsaugoti pakeitimus')}</button></div>
            </form>
          </ThirdPane>
        </div>
      ) : null}
    </PanelShell>
  );
}

/** Projektai as in the PHP panel: project families as small app-icon cards; the editor opens under a card. */
export function ProjectsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const projects = useProjects();
  const [openId, setOpenId] = useState<number | null>(null);
  const [notice, setNotice] = useState(false);
  const [logoVersion, setLogoVersion] = useState(() => Date.now());
  const createRef = useRef<HTMLDetailsElement>(null);
  const list = projects.data ?? [];
  const byId = new Map(list.map((x) => [x.id, x]));
  const roots = list.filter((x) => !x.parentProjectId || !byId.has(x.parentProjectId));
  const saved = () => { setNotice(true); setOpenId(null); if (createRef.current) createRef.current.open = false; };

  // As in the PHP panel: every project is its own card in the grid, a family's sub-projects right after it.
  const node = (row: Project, depth: number, seen: Set<number>): ReactNode[] => {
    seen.add(row.id);
    const kids = list.filter((c) => c.parentProjectId === row.id && !seen.has(c.id));
    const parent = row.parentProjectId ? byId.get(row.parentProjectId) : undefined;
    const isOpen = openId === row.id;
    return [
      <div key={row.id} className={`project-node project-node--${depth ? 'child' : 'parent'}`} style={{ '--depth': depth } as CSSProperties}>
        <article className="project-card" style={{ '--project': row.color } as CSSProperties}>
          <div className="project-logo">{row.hasLogo ? <img src={`api/v1/projects/${row.id}/logo?v=${logoVersion}`} alt="" /> : <span>{row.name.slice(0, 2).toUpperCase()}</span>}</div>
          <div><small>{parent ? `${t('Šeima')} · ${parent.name}` : t('Pagrindinis projektas')}</small><h2>{row.name}</h2><p>{row.emailFrom || t('El. paštas nenustatytas')}</p></div>
          <button type="button" className="button button--ghost button--small" aria-expanded={isOpen} onClick={() => setOpenId(isOpen ? null : row.id)}>{t('Redaguoti')}</button>
        </article>
        <div className={`panel-card hidden project-edit${isOpen ? ' is-open' : ''}`}>
          {isOpen ? <>
            <ProjectForm project={row} list={list} onSaved={saved} />
            <ProjectLogo project={row} version={logoVersion} onChange={() => setLogoVersion(Date.now())} />
          </> : null}
        </div>
      </div>,
      ...kids.filter((k) => !seen.has(k.id)).flatMap((k) => node(k, depth + 1, seen)),
    ];
  };
  const seen = new Set<number>();
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Struktūra')} title={t('Projektai')} text={t('Kurkite projektų šeimas. Kiekvienas antrinis projektas turi savo logotipą, el. paštą, paslaugas ir dokumentus.')} />}>
      {notice ? <Alert kind="success">{t('Projektas išsaugotas.')}</Alert> : null}
      <div className="two-cols two-cols--projects">
        <section className="panel-card project-list-pane">
          <div className="project-list-head"><p className="eyebrow">{t('Projektų šeimos')}</p><h2>{t('Projektai ir antriniai projektai')}</h2></div>
          {projects.isSuccess && !list.length ? <div className="empty-state compact"><h3>{t('Projektų dar nėra')}</h3><p>{t('Pridėkite pirmąjį projektą dešinėje.')}</p></div> : null}
          <div className="project-tree">{roots.flatMap((r) => node(r, 0, seen))}</div>
        </section>
        <aside className="project-manage-pane">
          <details className="panel-card project-create" ref={createRef}>
            <summary><div><p className="eyebrow">{t('Naujas')}</p><h2>{t('Pridėti projektą arba antrinį projektą')}</h2></div><span className="project-create-plus" aria-hidden="true">＋</span></summary>
            <div className="project-create-body"><ProjectForm project={null} list={list} onSaved={saved} /></div>
          </details>
        </aside>
      </div>
    </PanelShell>
  );
}

/** Create or edit one project (bank account and sending e-mail included); an existing one can be removed here. */
function ProjectForm({ project: p, list, onSaved }: { project: Project | null; list: Project[]; onSaved: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (body: unknown) => (p ? api.put(`api/v1/projects/${p.id}`, body) : api.post('api/v1/projects', body)),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['projects'] }); onSaved(); },
  });
  const remove = useMutation({ mutationFn: (id: number) => api.delete(`api/v1/projects/${id}`), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }) });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    const v = readForm(event);
    save.mutate({
      name: v.name, code: v.code, color: v.color || '#5C93CE', parentProjectId: v.parentProjectId ? Number(v.parentProjectId) : null,
      bankName: v.bankName, iban: v.iban, swift: v.swift, emailFrom: v.emailFrom, emailName: v.emailName, emailCopy: v.emailCopy === '1', smtpHost: v.smtpHost,
      smtpPort: v.smtpPort ? Number(v.smtpPort) : null, smtpSecurity: v.smtpSecurity || null, smtpUser: v.smtpUser,
      ...(v.smtpPassword ? { smtpPassword: v.smtpPassword } : {}),
    });
  };
  const error = save.error ?? remove.error;
  const [sendTest, setSendTest] = useState(true);
  // "Tikrinti ryšį": the mailbox as typed in the form (saved or not), step by step.
  const check = useMutation({
    mutationFn: (form: HTMLFormElement) => {
      const f = new FormData(form);
      const v = (k: string) => String(f.get(k) ?? '').trim();
      return api.post<SmtpCheck>('api/v1/projects/smtp-check', { projectId: p?.id ?? null, smtpHost: v('smtpHost'), smtpPort: Number(v('smtpPort') || 587), smtpSecurity: v('smtpSecurity') || 'tls',
        smtpUser: v('smtpUser'), smtpPassword: v('smtpPassword') || null, emailFrom: v('emailFrom'), emailName: v('emailName') || null, send: sendTest });
    },
  });
  return (
    <form key={p?.id ?? 'new'} onSubmit={submit}>
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      <div className="form-grid">
        <label>{t('Pavadinimas')}<input name="name" defaultValue={p?.name ?? ''} required /></label>
        <label>{t('Kodas')}<input name="code" defaultValue={p?.code ?? ''} pattern="[a-z0-9_\-]+" required /></label>
        <label>{t('Spalva')}<input type="color" name="color" defaultValue={p?.color ?? '#5C93CE'} /></label>
        <label>{t('Pagrindinis projektas')}<select name="parentProjectId" defaultValue={p?.parentProjectId ?? ''}><option value="">—</option>{list.filter((x) => x.id !== p?.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
      </div>
      <p className="form-section">{t('Projekto banko sąskaita')}</p>
      <div className="form-grid">
        <label>{t('Bankas')}<input name="bankName" defaultValue={p?.bankName ?? ''} /></label>
        <label>SWIFT / BIC<input name="swift" defaultValue={p?.swift ?? ''} /></label>
        <label className="span-2">IBAN<input name="iban" defaultValue={p?.iban ?? ''} /></label>
      </div>
      <p className="form-section">{t('Projekto el. paštas')}</p>
      <div className="form-grid">
        <label>{t('Siuntėjo el. paštas')}<input type="email" name="emailFrom" defaultValue={p?.emailFrom ?? ''} /></label>
        <label>{t('Siuntėjo vardas')}<input name="emailName" defaultValue={p?.emailName ?? ''} /></label>
        <label>{t('SMTP serveris')}<input name="smtpHost" defaultValue={p?.smtpHost ?? ''} /></label>
        <label>{t('Prievadas')}<input type="number" name="smtpPort" defaultValue={p?.smtpPort ?? 587} /></label>
        <label>{t('Šifravimas')}<select name="smtpSecurity" defaultValue={p?.smtpSecurity ?? 'tls'}><option value="tls">STARTTLS</option><option value="ssl">SSL/TLS</option><option value="none">{t('Nėra')}</option></select></label>
        <label>{t('SMTP naudotojas')}<input name="smtpUser" defaultValue={p?.smtpUser ?? ''} autoComplete="off" /></label>
        <label className="span-2">{t('SMTP slaptažodis')}<input type="password" name="smtpPassword" autoComplete="new-password" placeholder={p?.hasSmtpPassword ? t('Išsaugotas — palikite tuščią, kad nepasikeistų') : ''} /></label>
      </div>
      <label className="recurring-check"><input type="checkbox" name="emailCopy" value="1" defaultChecked={p?.emailCopy ?? false} />{t('Siųsti kiekvieno laiško kopiją siuntėjo adresu (matysite pašto dėžutėje)')}</label>
      <div className="smtp-check">
        <button type="button" className="button button--ghost button--small" disabled={check.isPending}
          onClick={(e) => check.mutate((e.currentTarget as HTMLButtonElement).form!)}>{check.isPending ? t('Tikrinama…') : t('Tikrinti ryšį')}</button>
        <label className="recurring-check"><input type="checkbox" checked={sendTest} onChange={(e) => setSendTest(e.target.checked)} />{t('Išsiųsti bandomąjį laišką man')}</label>
      </div>
      {check.isError ? <Alert kind="error">{errorText(t, check.error)}</Alert> : null}
      {check.data ? <SmtpResult result={check.data} /> : null}
      <div className="form-actions">
        <button className="button button--primary" disabled={save.isPending}>{t('Išsaugoti')}</button>
        {p ? <button type="button" className="button button--danger button--small" disabled={remove.isPending} onClick={() => { if (confirm(t('Pašalinti projektą „{name}“?', { name: p.name }))) remove.mutate(p.id); }}>{t('Pašalinti')}</button> : null}
      </div>
    </form>
  );
}

interface SmtpCheck { ok: boolean; steps: { step: 'resolve' | 'connect' | 'auth' | 'send'; ok: boolean }[]; failed?: { step: string; reason: string; detail: string }; ms: number; sentTo?: string }
const SMTP_STEPS: Record<string, string> = { resolve: 'Serverio adresas', connect: 'Prisijungimas ir šifravimas', auth: 'Naudotojas ir slaptažodis', send: 'Bandomasis laiškas' };
const SMTP_REASONS: Record<string, string> = {
  host_not_found: 'Serveris nerastas — patikrinkite SMTP serverio adresą.',
  host_not_allowed: 'Šis adresas neleidžiamas (vidinis tinklas).',
  port_not_allowed: 'Leidžiami tik SMTP prievadai: 25, 465, 587, 2525.',
  no_connection: 'Nepavyko prisijungti — patikrinkite prievadą (dažniausiai 587 arba 465) ir ar serveris pasiekiamas.',
  encryption: 'Šifravimas nesutampa — 465 prievadui rinkitės SSL/TLS, 587 — STARTTLS.',
  login_refused: 'Serveris nepriėmė naudotojo ar slaptažodžio. Gmail ar Outlook paskyroms reikia „programos slaptažodžio“.',
  sender_refused: 'Serveris neleido siųsti iš šio adreso — siuntėjo el. paštas turi priklausyti šiai pašto dėžutei.',
  other: 'Nepavyko patikrinti ryšio.',
};

/** The result of "Tikrinti ryšį": each step, and the reason where it stopped. */
function SmtpResult({ result }: { result: SmtpCheck }) {
  const { t } = useI18n();
  return (
    <div className={`smtp-result ${result.ok ? 'is-ok' : 'is-failed'}`} role="status">
      <strong>{result.ok ? `✓ ${t('Ryšys veikia')}` : `✗ ${t('Ryšys neveikia')}`}</strong> <small>{(result.ms / 1000).toFixed(1)} s</small>
      <ul>{result.steps.map((s) => <li key={s.step} className={s.ok ? 'ok' : 'fail'}>{s.ok ? '✓' : '✗'} {t(SMTP_STEPS[s.step] ?? s.step)}</li>)}</ul>
      {result.sentTo ? <p>{t('Bandomasis laiškas išsiųstas: {email}. Patikrinkite pašto dėžutę.', { email: result.sentTo })}</p> : null}
      {result.failed ? <p>{t(SMTP_REASONS[result.failed.reason] ?? SMTP_REASONS.other!)}{result.failed.detail ? <small>{t('Serverio atsakymas')}: {result.failed.detail}</small> : null}</p> : null}
    </div>
  );
}

/** The company's own logo, shown next to the greeting in its panel (also through a connected website). */
function CompanyLogo({ company }: { company: Company }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(() => Date.now());
  const done = async () => { setVersion(Date.now()); await Promise.all(['company', 'me'].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))); };
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch('api/v1/company/logo', { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new ApiError(response.status, data?.error?.code ?? 'server_error');
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api.delete('api/v1/company/logo'), onSuccess: done });
  const error = upload.error ?? remove.error;
  const code = error instanceof ApiError ? error.code : '';
  return (
    <div className="project-logo-field company-logo-field">
      <p className="form-section">{t('Įmonės logotipas')}</p>
      {error ? <Alert kind="error">{LOGO_ERRORS[code] ? t(LOGO_ERRORS[code]) : errorText(t, error)}</Alert> : null}
      <div className="project-logo-row">
        <div className="project-logo-preview">{company.hasLogo ? <img src={`api/v1/company/logo?v=${version}`} alt="" /> : <span>{t('Nėra')}</span>}</div>
        <label className="button button--ghost button--small">
          {upload.isPending ? t('Įkeliama…') : company.hasLogo ? t('Pakeisti') : t('Įkelti logotipą')}
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(ev) => { const f = ev.target.files?.[0]; if (f) upload.mutate(f); ev.target.value = ''; }} />
        </label>
        {company.hasLogo ? <button type="button" className="text-danger" onClick={() => remove.mutate()}>{t('Pašalinti')}</button> : null}
      </div>
      <small className="muted">{t('PNG, JPG arba WebP, iki 1 MB. Rodomas skydelyje prie pasisveikinimo.')}</small>
    </div>
  );
}

const LOGO_ERRORS: Record<string, string> = {
  file_type_not_allowed: 'Logotipas turi būti PNG, JPG arba WebP.',
  file_too_large: 'Logotipas per didelis (daugiausia 1 MB).',
};

/** Logo printed on this project's invoice PDFs; uploaded straight away, apart from the form. */
function ProjectLogo({ project, version, onChange }: { project: Project; version: number; onChange: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const done = async () => { onChange(); await queryClient.invalidateQueries({ queryKey: ['projects'] }); };
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`api/v1/projects/${project.id}/logo`, { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new ApiError(response.status, data?.error?.code ?? 'server_error');
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api.delete(`api/v1/projects/${project.id}/logo`), onSuccess: done });
  const error = upload.error ?? remove.error;
  const code = error instanceof ApiError ? error.code : '';
  return (
    <div className="project-logo-field">
      <p className="form-section">{t('Logotipas')}</p>
      {error ? <Alert kind="error">{LOGO_ERRORS[code] ? t(LOGO_ERRORS[code]) : errorText(t, error)}</Alert> : null}
      <div className="project-logo-row">
        <div className="project-logo-preview">{project.hasLogo ? <img src={`api/v1/projects/${project.id}/logo?v=${version}`} alt="" /> : <span>{t('Nėra')}</span>}</div>
        <label className="button button--ghost button--small">
          {upload.isPending ? t('Įkeliama…') : project.hasLogo ? t('Pakeisti') : t('Įkelti logotipą')}
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(ev) => { const f = ev.target.files?.[0]; if (f) upload.mutate(f); ev.target.value = ''; }} />
        </label>
        {project.hasLogo ? <button type="button" className="text-danger" onClick={() => remove.mutate()}>{t('Pašalinti')}</button> : null}
      </div>
      <small className="muted">{t('PNG, JPG arba WebP, iki 1 MB. Rodomas sąskaitų PDF viršuje.')}</small>
    </div>
  );
}

export function ServicesPage({ me }: { me: Me }) {
  const { t, locale } = useI18n();
  const services = useServices();
  const projects = useProjects();
  const e = useEditor<Service>('api/v1/services', 'services');
  const s = e.editing;
  const [invalid, setInvalid] = useState(false);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    const v = readForm(event);
    try {
      setInvalid(false);
      e.save.mutate({ name: v.name, description: nullIfEmpty(v.description), unit: v.unit || 'vnt.', unitPrice: cents(v.unitPrice), vatRate: v.vatCode ? chargedRate(v.vatCode, basisPoints(v.vatRate)) : basisPoints(v.vatRate), vatCode: v.vatCode || null, projectId: v.projectId ? Number(v.projectId) : null });
    } catch { setInvalid(true); }
  };
  const projectName = (id: number | null) => projects.data?.find((p) => p.id === id)?.name ?? '—';
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Dokumentai')} title={t('Paslaugos')} text={t('Dažnai naudojamos paslaugos greitam sąskaitų pildymui.')} />}>
      {e.notice ? <Alert kind="success">{t('Paslauga išsaugota.')}</Alert> : null}
      <div className="two-cols two-cols--form">
        <section className="panel-card"><div className="table-wrap"><table className="service-table">
          <thead><tr><th>{t('Paslauga')}</th><th>{t('Projektas')}</th><th>{t('Vnt.')}</th><th className="right">{t('Kaina')}</th><th /></tr></thead>
          <tbody>
            {services.isSuccess && !services.data.length ? <tr><td colSpan={5} className="empty">{t('Įrašų dar nėra.')}</td></tr> : null}
            {services.data?.map((row) => (
              <tr key={row.id}>
                <td><strong>{row.name}</strong><small>{row.description ?? ''}</small></td><td data-label={t('Projektas')}>{projectName(row.projectId)}</td><td>{row.unit}</td>
                <td className="right">{money(row.unitPrice)}</td>
                <td className="row-actions"><button type="button" className="text-link" onClick={() => e.setEditing(row)}>{t('Redaguoti')}</button><button type="button" className="text-danger" onClick={() => { if (confirm(t('Pašalinti paslaugą?'))) e.remove.mutate(row.id); }}>{t('Šalinti')}</button></td>
              </tr>
            ))}
          </tbody>
        </table></div></section>
        <EditorPane title={['Pridėti paslaugą', 'Redaguoti paslaugą']} editing={Boolean(s)} error={e.save.error} onCancel={() => e.setEditing(null)}>
          {invalid ? <Alert kind="error">{t('Patikrinkite pažymėtus laukus.')}</Alert> : null}
          <form key={s?.id ?? 'new'} onSubmit={submit}>
            <label>{t('Projektas')}<select name="projectId" defaultValue={s?.projectId ?? ''}><option value="">—</option>{projects.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
            <label>{t('Pavadinimas')}<input name="name" defaultValue={s?.name ?? ''} required /></label>
            <label>{t('Aprašymas')}<textarea name="description" rows={3} defaultValue={s?.description ?? ''} /></label>
            <div className="form-grid">
              <label>{t('Matavimo vnt.')}<input name="unit" defaultValue={s?.unit ?? 'vnt.'} /></label>
              <label>{t('Kaina (€)')}<input name="unitPrice" inputMode="decimal" defaultValue={moneyInput(s?.unitPrice)} required /></label>
              <label>{t('PVM %')}<input name="vatRate" inputMode="decimal" defaultValue={rateInput(s?.vatRate ?? 2100)} /></label>
              <label className="span-2">{t('PVM klasifikacija')}<select name="vatCode" defaultValue={s?.vatCode ?? ''}>
                <option value="">{t('Pagal PVM tarifą (Lietuvoje)')}</option>
                {VAT_CODES.filter((v) => v.issued && v.group !== 'domestic').map((v) => <option key={v.code} value={v.code}>{v[locale === 'lt' ? 'lt' : 'en']} · {v.code}</option>)}
              </select></label>
            </div>
            <div className="form-actions"><button className="button button--primary" disabled={e.save.isPending}>{t('Išsaugoti')}</button></div>
          </form>
        </EditorPane>
      </div>
    </PanelShell>
  );
}

export function TemplatesPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const templates = useTemplates();
  const projects = useProjects();
  const e = useEditor<Template>('api/v1/templates', 'templates');
  const tpl = e.editing;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    const v = readForm(event);
    e.save.mutate({ name: v.name, kind: v.kind, series: v.series, nextNumber: Number(v.nextNumber || 1), numberWidth: Number(v.numberWidth || 2), paymentDays: Number(v.paymentDays || 0), note: v.note ?? '', isDefault: v.isDefault === '1', projectId: v.projectId ? Number(v.projectId) : null });
  };
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Dokumentai')} title={t('Sąskaitų šablonai')} text={t('Redaguokite numeraciją, terminus ir numatytąsias pastabas arba pašalinkite nereikalingą šabloną.')} />}>
      {e.notice ? <Alert kind="success">{t('Šablonas išsaugotas.')}</Alert> : null}
      <div className="two-cols two-cols--form">
        <section className="panel-card"><div className="table-wrap"><table className="template-table">
          <thead><tr><th>{t('Šablonas')}</th><th>{t('Serija')}</th><th>{t('Kitas nr.')}</th><th>{t('Terminas')}</th><th /></tr></thead>
          <tbody>
            {templates.isSuccess && !templates.data.length ? <tr><td colSpan={5} className="empty">{t('Šablonų dar nėra.')}</td></tr> : null}
            {templates.data?.map((row) => (
              <tr key={row.id}>
                <td><strong>{row.name}{row.isDefault ? ' ★' : ''}</strong><small>{t(KIND_LABELS[row.kind])}</small></td><td data-label={t('Serija')}>{row.series}</td><td data-label={t('Kitas nr.')}>{row.nextNumber}</td><td data-label={t('Terminas')}>{row.paymentDays} {t('d.')}</td>
                <td className="row-actions"><button type="button" className="text-link" onClick={() => e.setEditing(row)}>{t('Redaguoti')}</button><button type="button" className="delete-x" aria-label={t('Pašalinti šabloną')} onClick={() => { if (confirm(t('Pašalinti šabloną „{name}“?', { name: row.name }))) e.remove.mutate(row.id); }}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table></div></section>
        <EditorPane title={['Pridėti šabloną', 'Redaguoti šabloną']} editing={Boolean(tpl)} error={e.save.error} onCancel={() => e.setEditing(null)}>
          <form key={tpl?.id ?? 'new'} onSubmit={submit}>
            <label>{t('Projektas')}<select name="projectId" defaultValue={tpl?.projectId ?? ''}><option value="">—</option>{projects.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
            <label>{t('Pavadinimas')}<input name="name" defaultValue={tpl?.name ?? ''} required /></label>
            <label>{t('Sąskaitos tipas')}<select name="kind" defaultValue={tpl?.kind ?? 'invoice'}>{Object.entries(KIND_LABELS).map(([k, label]) => <option key={k} value={k}>{t(label)}</option>)}</select></label>
            <div className="form-grid">
              <label>{t('Serija')}<input name="series" maxLength={12} defaultValue={tpl?.series ?? ''} required /></label>
              <label>{t('Kitas numeris')}<input type="number" name="nextNumber" min={1} defaultValue={tpl?.nextNumber ?? 1} /></label>
              <label>{t('Skaitmenų')}<input type="number" name="numberWidth" min={1} max={8} defaultValue={tpl?.numberWidth ?? 2} /></label>
              <label>{t('Apmokėjimo terminas')}<input type="number" name="paymentDays" min={0} defaultValue={tpl?.paymentDays ?? 7} /></label>
            </div>
            <label className="remember-device"><input type="checkbox" name="isDefault" value="1" defaultChecked={tpl?.isDefault ?? false} /><span>{t('Numatytasis')}</span></label>
            <label>{t('Pastaba')}<textarea name="note" rows={3} defaultValue={tpl?.note ?? 'Pardavėjas nėra PVM mokėtojas'} /></label>
            <div className="form-actions"><button className="button button--primary" disabled={e.save.isPending}>{t('Išsaugoti')}</button></div>
          </form>
        </EditorPane>
      </div>
    </PanelShell>
  );
}
